import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import QuoteCTA from "@/components/QuoteCTA";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import { BIZ } from "@/data/site";
import { coreServices, services } from "@/data/services";
import { ArrowRight, Calculator } from "lucide-react";

export default function ServicesHubPage() {
  const primary = coreServices();
  const secondary = services.filter((s) => !primary.includes(s));

  return (
    <div className="bg-paper min-h-screen">
      <Seo
        title={`Pressure Washing Services in ${BIZ.city}, ${BIZ.stateShort}`}
        description={`Full-service pressure washing and soft washing in ${BIZ.city}, ${BIZ.stateShort} and surrounding Middle Tennessee. House washing, roof cleaning, driveways, gutters, windows, and commercial exterior care.`}
        path="/pressure-washing-services/"
      />
      <MarketingNav />
      <QuoteCTA />

      <section className="pt-6 md:pt-8 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
              Services
            </div>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]"
              style={{ fontFamily: "Outfit" }}
            >
              Everything we do for<br />
              <span className="text-electric-500 italic">your home &amp; property.</span>
            </h1>
            <p className="mt-6 text-lg text-stone-600 max-w-xl">
              Residential and light-commercial exterior cleaning across {BIZ.region}. Right pressure, right detergent, right result.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/pressure-washing-estimate-calculator/"
                data-testid="hub-calculator-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand-600 hover:bg-electric-500 text-white font-bold px-6 py-3 transition-all hover:-translate-y-0.5 shadow-lg"
              >
                <Calculator className="w-4 h-4" />
                Get an instant estimate
              </Link>
              <a
                href="#quote"
                className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white text-stone-900 hover:border-stone-300 font-bold px-6 py-3 transition-all hover:-translate-y-0.5"
              >
                Request a written quote
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Primary services */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
            Our six core services
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight mb-10" style={{ fontFamily: "Outfit" }}>
            Where we spend most of our week.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hub-primary-services">
            {primary.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/${s.seoSlug}/`}
                  data-testid={`hub-service-${s.slug}`}
                  className="group p-8 rounded-3xl bg-white border border-stone-200 hover:border-electric-500 hover:shadow-xl transition-all flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-brand-100 group-hover:bg-electric-500 flex items-center justify-center transition-colors">
                      <Icon className="w-7 h-7 text-brand-700 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">
                        {s.priceLabel}
                      </div>
                      <div className="text-2xl font-extrabold text-stone-900" style={{ fontFamily: "Outfit" }}>
                        ${s.priceFrom}
                      </div>
                    </div>
                  </div>
                  <h3 className="mt-6 text-xl font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>
                    {s.seoTitle || s.title}
                  </h3>
                  <p className="mt-3 text-stone-600 leading-relaxed flex-1">{s.shortDesc}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-700 group-hover:text-electric-500 transition-colors">
                    Learn more
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Secondary — surface add-ons */}
      {secondary.length > 0 && (
        <section className="py-16 md:py-20 bg-stone-50 border-y border-stone-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4">
              Also on the truck
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-stone-900 tracking-tight mb-8" style={{ fontFamily: "Outfit" }}>
              Add-on surfaces we clean every week.
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="hub-secondary-services">
              {secondary.map((s) => {
                const Icon = s.icon;
                return (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="p-5 rounded-2xl bg-white border border-stone-200 hover:border-brand-300 hover:shadow-md transition-all group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center group-hover:bg-electric-500 transition-colors">
                      <Icon className="w-5 h-5 text-brand-700 group-hover:text-white transition-colors" />
                    </div>
                    <div className="mt-3 font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>
                      {s.title}
                    </div>
                    <div className="text-sm text-stone-500 mt-1">from ${s.priceFrom}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <QuoteForm />

      <footer className="bg-stone-900 text-stone-400 py-12 text-center text-xs">
        © {new Date().getFullYear()} {BIZ.name} · {BIZ.city}, {BIZ.stateShort} {BIZ.zip}
      </footer>
    </div>
  );
}
